import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getDefaultCompanyLogo, 
  setDefaultCompanyLogo, 
  applyDefaultLogoToAll,
  getCards,
  saveCard,
  deleteCard
} from '../utils/cardStorage';
import { NotificationManager } from '../components/Notification';
import '../styles/Settings.css';

const Settings = () => {
  const navigate = useNavigate();
  const logoInputRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [logoData, setLogoData] = useState({ data: '', size: 'medium' });
  const [applyToExisting, setApplyToExisting] = useState(false);
  
  useEffect(() => {
    // Charger le logo par défaut
    const defaultLogo = getDefaultCompanyLogo();
    if (defaultLogo) {
      setLogoData(defaultLogo);
    }
  }, []);
  
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoData({
          ...logoData,
          data: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSizeChange = (e) => {
    setLogoData({
      ...logoData,
      size: e.target.value
    });
  };
  
  const handleSaveLogo = () => {
    if (logoData.data) {
      setDefaultCompanyLogo(JSON.stringify(logoData));
      
      if (applyToExisting) {
        applyDefaultLogoToAll();
      }
      
      NotificationManager.show('Succès', 'Paramètres du logo enregistrés avec succès', 'success');
    }
  };
  
  const handleRemoveLogo = () => {
    setLogoData({ data: '', size: 'medium' });
    setDefaultCompanyLogo('');
    NotificationManager.show('Succès', 'Logo supprimé avec succès', 'success');
  };
  
  // Fonctions pour gérer l'export et l'import
  const handleExport = () => {
    const cards = getCards();
    if (cards.length === 0) {
      NotificationManager.show('Info', 'Aucune carte à exporter', 'error');
      return;
    }
    
    try {
      // Création d'un objet complet avec les cartes et les paramètres
      const exportData = {
        cards: cards,
        companySettings: {
          logo: logoData
        },
        exportDate: new Date().toISOString()
      };
      
      // Création du blob JSON
      const jsonBlob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(jsonBlob);
      
      // Téléchargement
      const link = document.createElement('a');
      link.href = url;
      link.download = `cartes_visite_export_${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      
      NotificationManager.show('Succès', `${cards.length} cartes exportées avec succès`, 'success');
    } catch (error) {
      console.error("Erreur lors de l'exportation:", error);
      NotificationManager.show('Erreur', "Impossible d'exporter les cartes", 'error');
    }
  };
  
  const handleImport = () => {
    fileInputRef.current.click();
  };
  
  const processImportFile = (file) => {
    const reader = new FileReader();
    reader.onload = function(e) {
      try {
        const data = JSON.parse(e.target.result);
        
        // Vérifier si la structure est valide
        if (data.cards && Array.isArray(data.cards)) {
          // Importer les cartes
          data.cards.forEach(card => {
            saveCard(card);
          });
          
          // Importer les paramètres de l'entreprise
          if (data.companySettings && data.companySettings.logo) {
            setDefaultCompanyLogo(JSON.stringify(data.companySettings.logo));
            setLogoData(data.companySettings.logo);
          }
          
          NotificationManager.show('Succès', `${data.cards.length} cartes importées avec succès`, 'success');
        } else {
          NotificationManager.show('Erreur', 'Format de fichier invalide', 'error');
        }
      } catch (error) {
        console.error("Erreur lors de l'importation:", error);
        NotificationManager.show('Erreur', "Impossible d'importer le fichier", 'error');
      }
    };
    reader.readAsText(file);
  };
  
  const handleDeleteAllCards = () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer TOUTES les cartes ? Cette action est irréversible.')) {
      const cards = getCards();
      let deleted = 0;
      
      cards.forEach(card => {
        deleteCard(card.id);
        deleted++;
      });
      
      NotificationManager.show('Succès', `${deleted} cartes supprimées avec succès`, 'success');
    }
  };
  
  return (
    <div className="container">
      <div className="main-content">
        <div className="settings-header">
          <button onClick={() => navigate('/')} className="back-btn">
            &larr; Retour
          </button>
          <h1>Paramètres</h1>
        </div>
        
        <div className="settings-content">
          <div className="settings-section">
            <h2>Logo d'entreprise par défaut</h2>
            <p className="settings-description">
              Définissez un logo d'entreprise par défaut qui sera automatiquement ajouté aux nouvelles cartes
              comportant le nom d'une entreprise.
            </p>
            
            <div className="logo-settings">
              <div className="upload-area" onClick={() => logoInputRef.current.click()}>
                <div className="preview-image" id="logoPreview">
                  {logoData.data ? (
                    <img src={logoData.data} alt="Logo par défaut" />
                  ) : (
                    <div className="photo-placeholder">
                      <div className="photo-icon">🏢</div>
                      <div className="photo-text">Logo</div>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={logoInputRef}
                  onChange={handleLogoChange}
                  accept="image/*"
                  className="file-input"
                />
                <p>Cliquez pour ajouter un logo</p>
              </div>
              
              <div className="logo-size-controls">
                <h3>Taille du logo</h3>
                <div className="size-selector">
                  <label>
                    <input 
                      type="radio" 
                      name="logoSize" 
                      value="small"
                      checked={logoData.size === 'small'}
                      onChange={handleSizeChange}
                    /> Petit
                  </label>
                  <label>
                    <input 
                      type="radio" 
                      name="logoSize" 
                      value="medium"
                      checked={logoData.size === 'medium'}
                      onChange={handleSizeChange}
                    /> Moyen
                  </label>
                  <label>
                    <input 
                      type="radio" 
                      name="logoSize" 
                      value="large"
                      checked={logoData.size === 'large'}
                      onChange={handleSizeChange}
                    /> Grand
                  </label>
                </div>
              </div>
              
              <div className="apply-options">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    checked={applyToExisting}
                    onChange={(e) => setApplyToExisting(e.target.checked)}
                  />
                  Appliquer ce logo aux cartes existantes qui ont une entreprise mais pas de logo
                </label>
              </div>
              
              <div className="logo-actions">
                {logoData.data && (
                  <button onClick={handleRemoveLogo} className="btn-danger">
                    Supprimer le logo
                  </button>
                )}
                <button 
                  onClick={handleSaveLogo} 
                  className="btn-success"
                  disabled={!logoData.data}
                >
                  Enregistrer les paramètres
                </button>
              </div>
            </div>
          </div>
          
          <div className="settings-section">
            <h2>Import / Export</h2>
            <p className="settings-description">
              Exportez vos cartes pour les sauvegarder ou les importer sur un autre appareil.
            </p>
            
            <div className="import-export-actions">
              <button onClick={handleExport} className="btn-primary">
                Exporter toutes les cartes
              </button>
              <button onClick={handleImport} className="btn-primary">
                Importer des cartes
              </button>
              <input
                type="file"
                ref={fileInputRef}
                className="file-input"
                accept=".json"
                onChange={(e) => {
                  if (e.target.files.length > 0) {
                    processImportFile(e.target.files[0]);
                    e.target.value = null; // Réinitialiser pour permettre de sélectionner le même fichier
                  }
                }}
              />
            </div>
          </div>
          
          <div className="settings-section">
            <h2>Gestion des données</h2>
            <p className="settings-description">
              Attention, ces actions sont irréversibles !
            </p>
            
            <div className="danger-zone">
              <button onClick={handleDeleteAllCards} className="btn-danger">
                Supprimer toutes les cartes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
